import { Alert, Button, Select, Spin } from "antd";
import axios from "axios";
import qs from "query-string";
import { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router";
import { getBackendUrl } from "../utils";

type LinkSubsidiary = { id: string; name: string };

/*
FND-3427: NetSuite subsidiary selector, shown after TBA OAuth completes when the
backend has the NETSUITE_LINK_SUBSIDIARY_SELECT flag on for the organization.

The backend redirects here with `?state=<token>`, the same Redis OAuth state
token that authenticated the OAuth callback. Unlike CompanySelect (Dynamics),
the options are fetched over XHR rather than packed into the URL: subsidiary
names contain commas and OneWorld lists can be long, so the comma-array URL
encoding is fragile.

Flow:
  1. GET  {backend}/netsuite/link/subsidiaries?state=<token>  → { subsidiaries }
  2. Exactly one option (non-OneWorld account) → auto-submit, no UI.
  3. Connect → full-page navigation to
     {backend}/netsuite/finish-subsidiary-select?state=<token>&subsidiaryId=<id>
     which persists the choice and runs the normal Link completion redirect.

The selection does not scope sync; Rutter still syncs every subsidiary.
*/
function SubsidiarySelect() {
  const history = useHistory();
  const { search } = history.location;
  const parsedSearch = qs.parse(search);
  const state =
    typeof parsedSearch.state === "string" ? parsedSearch.state : "";

  const [subsidiaries, setSubsidiaries] = useState<LinkSubsidiary[] | null>(
    null,
  );
  const [subsidiaryId, setSubsidiaryId] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const submit = useCallback(
    (id: string) => {
      setIsSubmitting(true);
      window.location.href = `${getBackendUrl()}/netsuite/finish-subsidiary-select?${qs.stringify(
        { state, subsidiaryId: id },
      )}`;
    },
    [state],
  );

  useEffect(() => {
    document.title = "Select your NetSuite subsidiary";
    if (!state) {
      history.replace("/error");
      return;
    }
    let cancelled = false;
    axios
      .get<{ subsidiaries: LinkSubsidiary[] }>(
        `${getBackendUrl()}/netsuite/link/subsidiaries`,
        { params: { state } },
      )
      .then((response) => {
        if (cancelled) return;
        const rawOptions = Array.isArray(response.data?.subsidiaries)
          ? response.data.subsidiaries
          : [];
        const options = rawOptions.filter(
          (subsidiary): subsidiary is LinkSubsidiary =>
            !!subsidiary && typeof subsidiary.id === "string",
        );
        if (options.length === 0) {
          history.replace("/error");
          return;
        }
        if (options.length === 1) {
          // Non-OneWorld account: nothing to choose, finish immediately.
          submit(options[0].id);
          return;
        }
        setSubsidiaries(options);
      })
      .catch((err) => {
        if (cancelled) return;
        const message = err?.response?.data?.error_message;
        if (typeof message === "string" && message) {
          setErrorMessage(message);
          return;
        }
        history.replace("/error");
      });
    return () => {
      cancelled = true;
    };
  }, [state, history, submit]);

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 99,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <div style={{ width: 800, maxWidth: "90vw" }}>
        {errorMessage && (
          <Alert
            message="Error"
            description={
              <div style={{ whiteSpace: "pre-line" }}>{errorMessage}</div>
            }
            type="error"
            showIcon
            style={{ marginBottom: 20 }}
          />
        )}

        {subsidiaries === null ? (
          !errorMessage && (
            <div style={{ textAlign: "center" }}>
              <Spin />
              <div style={{ marginTop: 12 }}>
                Loading subsidiaries from NetSuite…
              </div>
            </div>
          )
        ) : (
          <>
            <div className="font-semibold">
              Select the NetSuite subsidiary to connect:
            </div>

            <Select
              showSearch
              optionFilterProp="children"
              style={{ width: "100%", marginTop: 20, marginBottom: 20 }}
              placeholder="Select Subsidiary"
              onSelect={(val: any) => setSubsidiaryId(String(val))}
              defaultValue={undefined}
            >
              {subsidiaries.map((subsidiary) => (
                <Select.Option key={subsidiary.id} value={subsidiary.id}>
                  {subsidiary.name}
                </Select.Option>
              ))}
            </Select>
            <Button
              type="primary"
              block
              loading={isSubmitting}
              disabled={isSubmitting || !subsidiaryId}
              onClick={() => submit(subsidiaryId)}
            >
              Connect
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

export default SubsidiarySelect;
